import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { setCanonical, setMeta } from '@/utils/seo';
import { INDUSTRIES } from '@/data/industries';
import LeadForm from '@/components/LeadForm';
import { OrganizationSchema, WebSiteSchema, ServiceSchema, FAQSchema, HowToSchema, SpeakableSchema } from '@/components/SchemaMarkup';
import { ArrowRight, ChevronDown, ChevronUp, MapPin, Star, Lightbulb } from 'lucide-react';
import api from '@/utils/api';
import GoogleReviews from '@/components/GoogleReviews';

const INDUSTRY_IMAGES = {
  construction: '/images/construction.jpg',
  restaurants: '/images/restaurants.jpg',
  'home-health': '/images/home-health.jpg',
  landscaping: '/images/landscaping.jpg',
  trucking: '/images/trucking.jpg',
  'all-businesses': '/images/all-businesses.jpg'
};

const INDUSTRY_SLUG_MAP = { 'Construction': 'construction', 'Restaurants': 'restaurants', 'Home Health': 'home-health', 'Landscaping': 'landscaping', 'Trucking': 'trucking', 'All Businesses': 'all-businesses' };

export default function HomePage() {
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tip, setTip] = useState('');
  const [cityOfWeek, setCityOfWeek] = useState(null);

  useEffect(() => {
    setCanonical('/');
    setMeta('Compare Workers Comp Insurance | California Business Quotes | CompareWorkersComp.com',
      'Compare workers compensation insurance quotes from 50+ carriers for California businesses. Free quotes for construction, restaurants, home health, landscaping, trucking, and more.');
    api.get('/faqs/homepage').then(r => setFaqs(r.data.faqs)).catch(() => {});
    api.get('/tip-of-the-week').then(r => setTip(r.data.tip)).catch(() => {});
    api.get('/city-of-the-week').then(r => setCityOfWeek(r.data)).catch(() => {});
    const formTimer = setTimeout(() => setShowForm(true), 2500);
    return () => clearTimeout(formTimer);
  }, []);

  return (
    <>
      <OrganizationSchema /><WebSiteSchema />
      <ServiceSchema name="Workers Compensation Insurance Comparison" description="Compare workers comp quotes from 50+ carriers for California businesses" areaServed="California" />
      <HowToSchema /><SpeakableSchema name="CompareWorkersComp.com" url="https://compareworkerscomp.com" />
      {faqs.length > 0 && <FAQSchema faqs={faqs} />}

      {/* Hero — Dental-site technique: image behind, video on top with poster = same image */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Static image — loads first for page speed */}
        <div className="absolute inset-0">
          <img
            src="/hero-workers.png"
            alt="California workers from all industries smiling"
            className="w-full h-full object-cover"
            style={{ objectPosition: '50% 20%' }}
            loading="eager"
          />
          {/* Video on top — poster = same image, seamless transition when it plays */}
          <video
            autoPlay
            muted
            playsInline
            poster="/hero-workers.png"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: '50% 20%' }}
            data-testid="hero-video"
          >
            <source src="/hero-workers.mp4" type="video/mp4" />
          </video>
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-cwc-navy/65 via-cwc-navy/35 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-cwc-navy/30 via-transparent to-cwc-navy/10" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeInUp">
              {/* Speakable intro — subtle for SEO */}
              <p className="speakable-intro text-white/30 text-xs mb-5" data-testid="speakable-intro">
                CompareWorkersComp.com is a workers compensation insurance comparison resource that helps California business owners find the right coverage for their industry.
              </p>

              <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold text-white leading-tight mb-6 hero-text-shadow" data-testid="hero-h1">
                Compare Workers Comp<br />Insurance in <span className="text-red-500">California</span>
              </h1>
              <p className="text-white/90 text-lg mb-8 max-w-lg hero-text-shadow-sm">Get matched with licensed agents. Compare quotes from 50+ carriers. Find the right coverage at a competitive price.</p>

              {/* Find Your City button */}
              <div className="flex flex-wrap gap-3">
                <Link to="/cities" className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white px-5 py-3 rounded-full border border-white/25 hover:bg-white/25 transition-colors text-sm font-medium" data-testid="find-your-city-btn">
                  <MapPin className="w-4 h-4 text-red-400" /> Find Your City
                </Link>
                <a href="/#lead-form" onClick={() => { if (window.gtag) window.gtag('event', 'renewal_cta_click', { event_category: 'engagement', event_label: 'homepage' }); }} className="renewal-pulse inline-flex items-center gap-2 bg-green-500/20 backdrop-blur-sm text-white px-5 py-3 rounded-full border-2 border-green-400/40 hover:bg-green-500/30 transition-colors text-sm font-medium" data-testid="renewal-cta-btn">
                  Rates Change — Compare At Renewal
                </a>
              </div>
            </div>
            {/* Form slides in after delay */}
            <div id="lead-form" className={`lg:pl-8 transition-all duration-700 ease-out ${showForm ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} data-testid="hero-form">
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* Carriers Section */}
      <section className="bg-gray-50 py-14" id="carriers-section" data-testid="carriers-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm uppercase tracking-widest text-gray-400 font-medium mb-2">Our Partners</p>
          <h2 className="text-2xl md:text-3xl font-bold text-cwc-navy mb-2">Trusted Insurance Carriers</h2>
          <p className="text-gray-500 mb-8">We work with top-rated carriers to find you the best coverage and rates</p>
          <img src="/carriers.png" alt="Insurance Carriers - Travelers, Employers, The Zenith, The Hartford, Berkshire Hathaway Guard, Great American, Pie Insurance" className="max-w-full mx-auto" loading="lazy" data-testid="carriers-image" />
        </div>
      </section>

      {/* Google Reviews */}
      <section className="bg-white py-14" data-testid="home-reviews">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <GoogleReviews />
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white py-20" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-cwc-navy text-center mb-4">How It Works</h2>
          <p className="text-gray-500 text-center mb-14 max-w-2xl mx-auto">Get covered in four simple steps. No obligation, no cost.</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {['Tell Us Your Industry', 'Get Matched', 'Compare Quotes', 'Get Covered'].map((step, i) => (
              <div key={step} className="text-center group">
                <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5 group-hover:bg-red-100 transition-colors">
                  <span className="text-2xl font-bold text-red-600">{i + 1}</span>
                </div>
                <h3 className="font-semibold text-cwc-navy mb-2">{step}</h3>
                <p className="text-gray-500 text-sm">{['Select your business category and California city', 'We match you with licensed California insurance agents', 'Review competitive quotes from 50+ carriers side by side', 'Choose the best coverage and save on your premium'][i]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Cards */}
      <section className="bg-gray-50 py-20" id="industry-cards" data-testid="industry-cards">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-cwc-navy text-center mb-4">Coverage By Industry</h2>
          <p className="text-gray-500 text-center mb-14 max-w-2xl mx-auto">Every industry has different risks and rates. Find coverage tailored to yours.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES.map(ind => (
              <Link key={ind.slug} to={`/workers-comp-for-${ind.slug}-companies`} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all group border border-gray-100" data-testid={`industry-card-${ind.slug}`}>
                <div className="h-48 overflow-hidden">
                  <img src={INDUSTRY_IMAGES[ind.slug]} alt={`${ind.name} workers in California`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-cwc-navy text-lg mb-2 group-hover:text-red-600 transition-colors">{ind.name}</h3>
                  <p className="text-gray-500 text-sm mb-3">{ind.desc}</p>
                  <p className="text-xs font-medium text-red-600">{ind.avgCost} <ArrowRight className="w-3 h-3 inline ml-1" /></p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* City of the Week + Workers' Comp Tip of the Week */}
      <section className="bg-white py-16" data-testid="weekly-spotlight">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* City of the Week */}
            {cityOfWeek && (
              <div className="bg-green-50 rounded-2xl p-7 border border-green-200" data-testid="city-of-week">
                <div className="inline-flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5">
                  <Star className="w-3.5 h-3.5" /> City of the Week
                </div>
                <h3 className="text-2xl font-bold text-green-900 mb-2">{cityOfWeek.name}, CA</h3>
                <p className="text-green-700/60 text-sm mb-5">{cityOfWeek.county} | Pop. {cityOfWeek.pop}</p>
                <div className="flex flex-wrap gap-2">
                  {cityOfWeek.industries.map(ind => {
                    const slug = INDUSTRY_SLUG_MAP[ind] || 'all-businesses';
                    return (
                      <Link key={ind} to={`/workers-comp-for-${slug}-in-${cityOfWeek.slug}-ca`} className="bg-green-100 text-green-700 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-green-200 transition-colors border border-green-200" data-testid={`cotw-${slug}`}>
                        {ind} in {cityOfWeek.name}
                      </Link>
                    );
                  })}
                  <Link to={`/workers-comp-insurance-${cityOfWeek.slug}-ca`} className="bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors" data-testid="cotw-all">
                    All Coverage <ArrowRight className="w-3 h-3 inline ml-1" />
                  </Link>
                </div>
              </div>
            )}

            {/* Workers' Comp Tip of the Week */}
            {tip && (
              <div className="bg-green-50 rounded-2xl p-7 border border-green-200" data-testid="tip-of-week">
                <div className="inline-flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5">
                  <Lightbulb className="w-3.5 h-3.5" /> Workers' Comp Tip of the Week
                </div>
                <p className="text-green-900 text-lg leading-relaxed font-medium">{tip}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="bg-gray-50 py-20" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-cwc-navy text-center mb-14">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-gray-200 rounded-xl overflow-hidden bg-white" data-testid={`faq-item-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left font-medium text-cwc-navy hover:bg-gray-50 transition-colors">
                    <span className="pr-4">{faq.q}</span>
                    {openFaq === i ? <ChevronUp className="w-5 h-5 shrink-0 text-red-500" /> : <ChevronDown className="w-5 h-5 shrink-0 text-gray-400" />}
                  </button>
                  {openFaq === i && <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured In */}
      <section className="bg-white py-12" data-testid="featured-in">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm uppercase tracking-widest text-gray-400 font-medium mb-4">Trio Insurance Featured In</p>
          <img src="/featured-in.png" alt="Trio Insurance featured in FOX, Google News, Digital Journal" className="max-w-md mx-auto" loading="lazy" data-testid="featured-in-image" />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cwc-navy py-20" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Compare Quotes?</h2>
          <p className="text-white/60 mb-8">Join thousands of California businesses saving on workers comp insurance.</p>
          <a href="/#lead-form" className="inline-flex bg-red-600 text-white font-bold px-8 py-4 rounded-full hover:bg-red-700 transition-colors text-lg" data-testid="cta-button">Get Free Quotes Now</a>
        </div>
      </section>
    </>
  );
}
